
import React, { useState, useEffect } from 'react';
import { Button, Table, Spinner } from 'react-bootstrap';
import TablePaginationBar from '../components/TablePaginationBar';
import { BsThreeDots } from 'react-icons/bs';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useData } from '../context/DataContext';
import { config } from '../config/config';
import { getarchivedproviders } from '../config/url';
import useFetchData from '../hooks/useFetchData';
import { Link } from 'react-router-dom';

const InactiveProviders = () => {
  const [render, setRender] = useState(null);
  const [currentProviders, setCurrentProviders] = useState(null);
  const [providers, setProviders] = useState([]); // List of providers
  const [search, setSearch] = useState(''); // Search input value
  const [filter, setFilter] = useState(''); // Filter value
  const [currentPage, setCurrentPage] = useState(1); // Current page
  const [providersPerPage, setProvidersPerPage] = useState(10);
  const [sortField, setSortField] = useState("name"); // "name" or "date"
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"

  // Fetch provider data
  const { data, loading } = useFetchData(getarchivedproviders, config);

  // Sort function
  const sortProviders = (providersToSort) => {
    return [...providersToSort].sort((a, b) => {
      let aValue, bValue;

      if (sortField === "name") {
        aValue = `${a.firstname || ""} ${a.lastname || ""}`.toLowerCase();
        bValue = `${b.firstname || ""} ${b.lastname || ""}`.toLowerCase();
      } else if (sortField === "date") {
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <FaSort style={{ marginLeft: "5px", opacity: 0.5 }} />;
    }
    return sortDirection === "asc" ? 
      <FaSortUp style={{ marginLeft: "5px" }} /> : 
      <FaSortDown style={{ marginLeft: "5px" }} />;
  };

  // Pagination
  useEffect(() => {
    const indexOfLastProvider = currentPage * providersPerPage;
    const indexOfFirstProvider = indexOfLastProvider - providersPerPage;
    setCurrentProviders(
      providers.slice(indexOfFirstProvider, indexOfLastProvider)
    );
  }, [currentPage, providers, providersPerPage, render]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Filter and search
  useEffect(() => {
    // Only update providers when loading is complete
    if (loading === false) {
      if (data && Array.isArray(data) && data.length > 0) {
        let filteredProviders = data;
        
        if (filter === 'Sort by Type') {
          filteredProviders = [...data].sort((a, b) => (a.type > b.type ? 1 : -1));
        } else if (filter === 'Sort by State') {
          filteredProviders = [...data].sort((a, b) => (a.state > b.state ? 1 : -1));
        }

        if (search) {
          filteredProviders = filteredProviders.filter((provider) =>
            Object.values(provider)
              .join(' ')
              .toLowerCase()
              .includes(search.toLowerCase())
          );
        }

        // Apply sorting
        filteredProviders = sortProviders(filteredProviders);

        setProviders(filteredProviders);
        setRender(Math.floor(Math.random() * 10));
      } else {
        // Explicitly set empty array when response comes back with no providers or null/undefined
        setProviders([]);
        setRender(Math.floor(Math.random() * 10));
      }
    }
  }, [search, filter, data, sortField, sortDirection, loading]);

  return (
    <div className='container'>
      <div className='d-flex justify-content-between align-items-center'>
        <div>
          <Link to="/providers" state={{ openAddDrawer: true }}>
            <Button variant='primary'>Add Provider</Button>
          </Link>
        </div>

        {!loading && (
          <div className='d-flex'>
            <div className='me-2'>
              <input
                type='text'
                className='form-control rounded'
                placeholder='Search by ID, name, type...'
                style={{ maxWidth: '300px' }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div>
              <select
                className='form-select'
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value=''>Select Filter</option>
                <option value='Sort by Type'>Sort by Type</option>
                <option value='Sort by State'>Sort by State</option>
              </select>
            </div>
          </div>
        )}
      </div>
      <div className='bg-white my-4 rounded shadow'>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">Loading inactive providers...</div>
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center p-4">No inactive providers found.</div>
        ) : (
          <Table
            responsive='xl'
            className='table table-hover mb-4'
            style={{ borderCollapse: 'separate', borderSpacing: '0 1rem' }}
          >
            <thead className='bg-light'>
              <tr>
                <th 
                  style={{ 
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "background-color 0.2s ease"
                  }}
                  onClick={() => handleSort("name")}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#e9ecef";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#f8f9fa";
                  }}
                >
                  Name {getSortIcon("name")}
                </th>
                <th>Type</th>
                <th>Provider Type</th>
                <th>Gender</th>
                <th>State</th>
                <th>Assigned Users</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {currentProviders && currentProviders.map((provider) => (
                <tr key={provider.id}>
                  <td>{provider.firstname} {provider.lastname}</td>
                  <td>{provider.specialisation}</td>
                  <td>{provider.providerType || "Therapist"}</td>
                  <td>{provider.gender}</td>
                  <td>{provider.state}</td>
                  <td>{provider.users.length}</td>
                  <td>
                    <Link
                      to={`/providers/${provider.id}`}
                      state={{ provider: provider }}
                    >
                      <Button variant='secondary' size='md'>
                        <BsThreeDots />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
      {!loading && providers.length > 0 && (
        <TablePaginationBar
          currentPage={currentPage}
          totalItems={providers.length}
          rowsPerPage={providersPerPage}
          onPageChange={paginate}
          onRowsPerPageChange={(n) => {
            setProvidersPerPage(n);
            setCurrentPage(1);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </div>
  );
};

export default InactiveProviders;
